import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import { adsDisplayService } from './adsDisplayService';
import {
  assertContentIsAllowed,
  moderateImage,
  ContentModerationError,
} from '../lib/contentModeration';

// File upload function
export const uploadPostFile = async (file: File, postId: string): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${postId}/${Date.now()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('posts')
    .upload(fileName, file);

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from('posts')
    .getPublicUrl(fileName);

  return data.publicUrl;
};

export const postsService = {
  async getPosts(options?: {
    category?: ArtCategory | 'all';
    sortBy?: 'recent' | 'popular' | 'trending';
    searchQuery?: string;
    limit?: number;
    offset?: number;
    includeAds?: boolean;
    moderationStatus?: 'pending' | 'approved' | 'rejected' | 'all';
  }) {
    console.log('📰 PostsService: Getting posts with options:', options);

    let query = supabase
      .from('posts')
      .select(`
        *,
        profiles!posts_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type
        ),
        media_files (
          id,
          type,
          url,
          file_name,
          file_size,
          duration,
          width,
          height
        ),
        comments (
          id,
          content,
          author_id,
          likes_count,
          created_at,
          moderation_status,
          moderation_reason,
          profiles!comments_author_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `);

    const moderationStatus = options?.moderationStatus ?? 'approved';

    if (moderationStatus !== 'all') {
      query = query.eq('moderation_status', moderationStatus);
    }

    // Filter by category
    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }

    // Search functionality
    if (options?.searchQuery && options.searchQuery.trim()) {
      const searchTerm = options.searchQuery.trim();
      query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`);
    }

    // Sort
    switch (options?.sortBy) {
      case 'popular':
        query = query.order('likes_count', { ascending: false });
        break;
      case 'trending':
        query = query.order('views_count', { ascending: false });
        break;
      case 'recent':
      default:
        query = query.order('created_at', { ascending: false });
        break;
    }

    // Apply premium visibility rules
    const { data: auth } = await supabase.auth.getUser();
    const viewerId = auth?.user?.id || null;
    let viewerIsPremium = false;

    if (viewerId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', viewerId)
        .maybeSingle();

      viewerIsPremium = !!profile?.is_premium;
    }

    if (!viewerIsPremium) {
      if (viewerId) {
        query = query.or(`is_premium.eq.false,author_id.eq.${viewerId}`);
      } else {
        query = query.eq('is_premium', false);
      }
    }

    // Pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;
    if (error) throw error;

    const organicPosts = (data || []).map((post) => {
      const comments = Array.isArray((post as { comments?: Array<{ moderation_status?: string; author_id: string }> }).comments)
        ? (post as { comments: Array<{ moderation_status?: string; author_id: string }> }).comments
        : [];

      const filteredComments = comments.filter((comment: { moderation_status?: string }) => {
        if (!comment?.moderation_status || moderationStatus === 'all') return true;
        return comment.moderation_status === 'approved';
      });

      return {
        ...post,
        comments: filteredComments,
      };
    });
    console.log('📰 PostsService: Loaded', organicPosts.length, 'organic posts');

    // Mix with ads if requested
    if (options?.includeAds !== false && organicPosts.length >= 3) {
      console.log('🎯 PostsService: Including ads in posts, organic posts:', organicPosts.length);
      const { data: { user } } = await supabase.auth.getUser();
      const mixedPosts = await adsDisplayService.mixPostsWithAds(organicPosts, user?.id, 3); // Show ad every 3 posts
      console.log('✅ PostsService: Mixed posts result:', mixedPosts.length, 'total posts (including ads)');
      return mixedPosts;
    }

    console.log('ℹ️ PostsService: Returning', organicPosts.length, 'organic posts only');
    return organicPosts;
  },

  async getPostById(id: string, options?: { bypassModeration?: boolean }) {
    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles!posts_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type
        ),
        media_files (
          id,
          type,
          url,
          file_name,
          file_size,
          duration,
          width,
          height
        ),
        comments (
          id,
          content,
          author_id,
          likes_count,
          created_at,
          moderation_status,
          moderation_reason,
          profiles!comments_author_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    if (!options?.bypassModeration) {
      const { data: auth } = await supabase.auth.getUser();
      const viewerId = auth?.user?.id;

      const hasNonApprovedComments = Array.isArray(data?.comments) && data.comments.some(
        (comment: { moderation_status?: string }) =>
          comment?.moderation_status && comment.moderation_status !== 'approved'
      );

      let viewerRole: string | null = null;
      let viewerIsPremium = false;

      if (
        viewerId &&
        ((data?.moderation_status && data.moderation_status !== 'approved') || hasNonApprovedComments)
      ) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type, is_premium')
          .eq('id', viewerId)
          .maybeSingle();

        viewerRole = profile?.user_type || null;
        viewerIsPremium = !!profile?.is_premium;
      }

      if (data?.moderation_status && data.moderation_status !== 'approved') {
        const isAuthor = viewerId && data.author_id === viewerId;
        const isModerator = viewerRole === 'moderator' || viewerRole === 'admin';

        if (!isAuthor && !isModerator) {
          throw new Error('Esta publicación está pendiente de revisión.');
        }
      }

      if (data?.is_premium) {
        const isAuthor = viewerId && data.author_id === viewerId;
        const isModerator = viewerRole === 'moderator' || viewerRole === 'admin';

        if (!isAuthor && !isModerator && !viewerIsPremium) {
          throw new Error('Esta publicación es exclusiva para usuarios premium.');
        }
      }

      if (Array.isArray(data?.comments)) {
        const isModerator = viewerRole === 'moderator' || viewerRole === 'admin';

        data.comments = data.comments.filter((comment: { moderation_status?: string; author_id: string }) => {
          if (!comment?.moderation_status || comment.moderation_status === 'approved') return true;
          if (viewerId && comment.author_id === viewerId) return true;
          return isModerator;
        });
      }
    }

    return data;
  },

  async createPost(postData: {
    title: string;
    description: string;
    category: ArtCategory;
    tags?: string[];
    forSale?: boolean;
    price?: number;
    isPremium?: boolean;
    files?: File[];
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([
      postData.title,
      postData.description,
      ...(postData.tags || []),
    ]);

    const { data, error } = await supabase
      .from('posts')
      .insert({
        author_id: user.id,
        title: postData.title,
        description: postData.description,
        category: postData.category,
        tags: postData.tags || [],
        for_sale: postData.forSale || false,
        price: postData.price || null,
        is_premium: postData.isPremium || false,
      })
      .select()
      .single();

    if (error) throw error;

    // Upload files if provided
    if (postData.files && postData.files.length > 0) {
      const mediaFiles = [];

      for (const file of postData.files) {
        try {
          // Anexo D.2.14 — moderar imágenes pre-upload con moderateImage()
          // (Google Vision SAFE_SEARCH_DETECTION si hay API key; de lo contrario
          // es no-op y devuelve []). Si se detectan categorías bloqueantes se
          // lanza ContentModerationError y se aborta el resto de la subida.
          if (file.type.startsWith('image/')) {
            const imageMatches = await moderateImage(file);
            const blocking = imageMatches.filter(
              (m) => m.category === 'adult' || m.category === 'violence' || m.category === 'racy',
            );
            if (blocking.length > 0) {
              throw new ContentModerationError(blocking);
            }
          }

          const url = await uploadPostFile(file, data.id);

          // Determine file type
          let fileType = 'document';
          if (file.type.startsWith('image/')) fileType = 'image';
          else if (file.type.startsWith('video/')) fileType = 'video';
          else if (file.type.startsWith('audio/')) fileType = 'audio';
          else if (file.name.toLowerCase().includes('.obj') ||
            file.name.toLowerCase().includes('.fbx') ||
            file.name.toLowerCase().includes('.gltf')) fileType = '3d';

          const mediaFile = {
            post_id: data.id,
            type: fileType,
            url: url,
            file_name: file.name,
            file_size: file.size,
            width: null,
            height: null,
            duration: null
          };

          mediaFiles.push(mediaFile);
        } catch (err) {
          console.error('Error uploading file:', file.name, err);
        }
      }

      // Insert media files
      if (mediaFiles.length > 0) {
        const { error: mediaError } = await supabase
          .from('media_files')
          .insert(mediaFiles);

        if (mediaError) {
          console.error('Error inserting media files:', mediaError);
        }
      }
    }

    return data;
  },

  async likePost(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First check if already liked to prevent duplicates
    const isAlreadyLiked = await this.checkIfLiked(postId);
    if (isAlreadyLiked) {
      throw new Error('Post already liked');
    }

    const { data, error } = await supabase
      .from('likes')
      .insert({
        user_id: user.id,
        post_id: postId,
      });

    if (error) throw error;

    // Note: Notification will be created automatically by database trigger
    return data;
  },

  async unlikePost(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('likes')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);

    if (error) throw error;
  },

  async deleteComment(commentId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.rpc('soft_delete_comment', {
      comment_id: commentId
    });

    if (error) throw error;

    if (data?.error) {
      throw new Error(data.error);
    }

    return data;
  },

  async checkIfLiked(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('likes')
      .select('id')
      .eq('user_id', user.id)
      .eq('post_id', postId)
      .limit(1);

    if (error) throw error;
    return data && data.length > 0;
  },

  async addComment(postId: string, content: string, parentCommentId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([content]);

    const { data, error } = await supabase
      .from('comments')
      .insert({
        post_id: postId,
        author_id: user.id,
        content,
        parent_comment_id: parentCommentId || null,
      })
      .select(`
        *,
        profiles!comments_author_id_fkey (
          username,
          full_name,
          avatar_url
        )
      `)
      .single();

    if (error) throw error;

    // Note: Notification will be created automatically by database trigger
    return data;
  },

  async updateCommentStatus(commentId: string, status: 'hidden' | 'approved' | 'rejected' | 'pending') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Optional: Add a check to ensure the user is a moderator
    const { data: profile } = await supabase.from('profiles').select('user_type').eq('id', user.id).single();
    if (profile?.user_type !== 'admin' && profile?.user_type !== 'moderator') {
      throw new Error('You do not have permission to perform this action.');
    }

    const { data, error } = await supabase
      .from('comments')
      .update({
        moderation_status: status,
        moderated_at: new Date().toISOString(),
        moderated_by: user.id,
      })
      .eq('id', commentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async toggleCommentLike(commentId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if already liked
    const { data: existingLike } = await supabase
      .from('likes')
      .select('id')
      .eq('user_id', user.id)
      .eq('comment_id', commentId)
      .maybeSingle();

    if (existingLike) {
      // Unlike
      const { error } = await supabase
        .from('likes')
        .delete()
        .eq('id', existingLike.id);

      if (error) throw error;
      return { liked: false };
    } else {
      // Like
      const { error } = await supabase
        .from('likes')
        .insert({
          user_id: user.id,
          comment_id: commentId,
        });

      if (error) throw error;
      return { liked: true };
    }
  },

  async getLikedCommentIds(commentIds: string[]) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || commentIds.length === 0) return [];

    const { data, error } = await supabase
      .from('likes')
      .select('comment_id')
      .eq('user_id', user.id)
      .in('comment_id', commentIds);

    if (error) {
      console.error('Error fetching liked comments:', error);
      return [];
    }

    return data.map(item => item.comment_id);
  },

  async incrementViews(postId: string) {
    try {
      // Use a simple update instead of RPC if the function doesn't exist
      const { data: post, error: fetchError } = await supabase
        .from('posts')
        .select('views_count')
        .eq('id', postId)
        .single();

      if (fetchError) {
        console.error('Error fetching post for view increment:', fetchError);
        return;
      }

      const { error: updateError } = await supabase
        .from('posts')
        .update({
          views_count: (post.views_count || 0) + 1
        })
        .eq('id', postId);

      if (updateError) {
        console.error('Error incrementing views:', updateError);
      }
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  },

  async deletePost(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      console.log('Starting post deletion for ID:', postId);

      // First, get the post to verify ownership and gather info
      const { data: post, error: fetchError } = await supabase
        .from('posts')
        .select(`
          *,
          media_files (
            id,
            url,
            file_name
          )
        `)
        .eq('id', postId)
        .eq('author_id', user.id)
        .single();

      if (fetchError) {
        console.error('Post fetch error:', fetchError);
        throw new Error(`Post not found or you don't have permission: ${fetchError.message}`);
      }

      console.log('Post found, proceeding with deletion. Media files:', post.media_files?.length || 0);

      // Delete media files from storage
      if (post.media_files && post.media_files.length > 0) {
        const filesToDelete = post.media_files.map((file: { url: string }) => {
          // Extract file path from URL
          const url = new URL(file.url);
          const pathParts = url.pathname.split('/');
          // Get the path after '/storage/v1/object/public/posts/'
          const bucketPath = pathParts.slice(6).join('/');
          return bucketPath;
        }).filter((path: string) => path); // Remove empty paths

        if (filesToDelete.length > 0) {
          console.log('Deleting files from storage:', filesToDelete);
          const { error: storageError } = await supabase.storage
            .from('posts')
            .remove(filesToDelete);

          if (storageError) {
            console.error('Error deleting files from storage:', storageError);
            // Continue with database deletion but warn user
          }
        }
      }

      // Delete media_files records from database
      console.log('Deleting media_files records...');
      const { error: mediaError } = await supabase
        .from('media_files')
        .delete()
        .eq('post_id', postId);

      if (mediaError) {
        console.error('Error deleting media files records:', mediaError);
        throw new Error(`Failed to delete media files: ${mediaError.message}`);
      }

      // Delete comments
      console.log('Deleting comments...');
      const { error: commentsError } = await supabase
        .from('comments')
        .delete()
        .eq('post_id', postId);

      if (commentsError) {
        console.error('Error deleting comments:', commentsError);
        throw new Error(`Failed to delete comments: ${commentsError.message}`);
      }

      // Delete likes
      console.log('Deleting likes...');
      const { error: likesError } = await supabase
        .from('likes')
        .delete()
        .eq('post_id', postId);

      if (likesError) {
        console.error('Error deleting likes:', likesError);
        throw new Error(`Failed to delete likes: ${likesError.message}`);
      }

      // Delete from wishlists
      console.log('Deleting from wishlists...');
      const { error: wishlistError } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('post_id', postId);

      if (wishlistError) {
        console.error('Error deleting from wishlists:', wishlistError);
        throw new Error(`Failed to delete from wishlists: ${wishlistError.message}`);
      }

      // Delete from cart items
      console.log('Deleting from cart...');
      const { error: cartError } = await supabase
        .from('cart_items')
        .delete()
        .eq('post_id', postId);

      if (cartError) {
        console.error('Error deleting from cart:', cartError);
        throw new Error(`Failed to delete from cart: ${cartError.message}`);
      }

      // Delete from collection_posts
      console.log('Deleting from collections...');
      const { error: collectionError } = await supabase
        .from('collection_posts')
        .delete()
        .eq('post_id', postId);

      if (collectionError) {
        console.error('Error deleting from collections:', collectionError);
        throw new Error(`Failed to delete from collections: ${collectionError.message}`);
      }

      // Finally, delete the post
      console.log('Deleting main post record...');
      const { error: deleteError } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId)
        .eq('author_id', user.id);

      if (deleteError) {
        console.error('Post deletion error:', deleteError);
        throw new Error(`Error deleting post: ${deleteError.message}`);
      }

      // Verify deletion
      console.log('Verifying post deletion...');
      const { data: verifyPost, error: verifyError } = await supabase
        .from('posts')
        .select('id')
        .eq('id', postId)
        .maybeSingle();

      if (verifyError) {
        console.error('Error verifying deletion:', verifyError);
      }

      if (verifyPost) {
        throw new Error('Post still exists after deletion attempt');
      }

      console.log('Post successfully deleted from database');
      return true;
    } catch (error) {
      console.error('Complete deletion error:', error);
      throw error;
    }
  },

  async updatePostStatus(postId: string, status: 'hidden' | 'approved' | 'rejected' | 'pending') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Optional: Add a check to ensure the user is a moderator
    const { data: profile } = await supabase.from('profiles').select('user_type').eq('id', user.id).single();
    if (profile?.user_type !== 'admin' && profile?.user_type !== 'moderator') {
      throw new Error('You do not have permission to perform this action.');
    }

    const { data, error } = await supabase
      .from('posts')
      .update({
        moderation_status: status,
        moderated_at: new Date().toISOString(),
        moderated_by: user.id,
      })
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updatePost(postId: string, updates: {
    title?: string;
    description?: string;
    category?: string;
    tags?: string[];
    for_sale?: boolean;
    price?: number;
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('posts')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', postId)
      .eq('author_id', user.id) // Ensure user can only edit their own posts
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async searchUsers(query: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, full_name, avatar_url')
      .or(`username.ilike.%${query}%,full_name.ilike.%${query}%`)
      .limit(10);

    if (error) throw error;
    return data || [];
  },

  async searchPosts(query: string) {
    let searchQuery = supabase
      .from('posts')
      .select(`
        *,
        profiles!posts_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type
        ),
        media_files (
          id,
          type,
          url,
          file_name
        )
      `)
      .or(`title.ilike.%${query}%,description.ilike.%${query}%,tags.cs.{${query}}`)
      .order('created_at', { ascending: false });

    const { data: auth } = await supabase.auth.getUser();
    const viewerId = auth?.user?.id || null;
    let viewerIsPremium = false;

    if (viewerId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', viewerId)
        .maybeSingle();

      viewerIsPremium = !!profile?.is_premium;
    }

    if (!viewerIsPremium) {
      if (viewerId) {
        searchQuery = searchQuery.or(`is_premium.eq.false,author_id.eq.${viewerId}`);
      } else {
        searchQuery = searchQuery.eq('is_premium', false);
      }
    }

    const { data, error } = await searchQuery;
    if (error) throw error;
    return data;
  },

  async addToWishlist(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wishlist_items')
      .insert({
        user_id: user.id,
        post_id: postId,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeFromWishlist(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('user_id', user.id)
      .eq('post_id', postId);

    if (error) throw error;
  },

  async checkIfSaved(postId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('wishlist_items')
      .select('id')
      .eq('user_id', user.id)
      .eq('post_id', postId)
      .limit(1);

    if (error) throw error;
    return data && data.length > 0;
  },
};
